import React from 'react';
import { MdOutlineGavel } from 'react-icons/md';
import { SectionCard, Pill, Chip, Note, FactGrid, StatTile, SubHeading, Disclosure } from './ui';
import { fmtMoney, fmtDate, fmtPct, titleCase, transactionTone, safeArray, nonEmptyRecord, isNum, DASH } from '../format';

function MortgageLine({ mortgage }) {
  if (!nonEmptyRecord(mortgage)) return null;
  const bits = [
    isNum(mortgage.amount_usd) ? fmtMoney(mortgage.amount_usd) : null,
    mortgage.loan_type ? titleCase(mortgage.loan_type) : null,
    mortgage.rate_type ? titleCase(mortgage.rate_type) : null,
    isNum(mortgage.interest_rate_percent) ? fmtPct(mortgage.interest_rate_percent, 2) : null,
    isNum(mortgage.term_months) ? `${mortgage.term_months} mo` : null,
  ].filter(Boolean);
  return (
    <div className="le-mortgage">
      <span className="le-mortgage-label">Mortgage</span>
      {mortgage.lender ? <strong>{mortgage.lender}</strong> : null}
      {bits.length ? <span className="muted"> · {bits.join(' · ')}</span> : null}
    </div>
  );
}

export default function DeedHistoryCard({ data, status }) {
  const transactions = safeArray(data?.transactions).slice().sort((a, b) => {
    const ad = a.recording_date || a.transaction_date || '';
    const bd = b.recording_date || b.transaction_date || '';
    return ad < bd ? 1 : ad > bd ? -1 : 0;
  });
  const foreclosures = safeArray(data?.foreclosure_records).filter(nonEmptyRecord);
  const notes = safeArray(data?.notes);
  const priceHistory = safeArray(data?.price_history);
  const taxHistory = safeArray(data?.tax_history);
  const lastSale = data?.last_sale || null;
  const subtitle = data ? 'County-recorded deeds, mortgages and filings, plus the listing record’s price and tax history · no person names' : null;

  return (
    <SectionCard id="le-section-deeds" icon={<MdOutlineGavel size={20} />} title="Deeds & history" subtitle={subtitle} status={status} dataset={data}>
      {data ? (
        <>
          <div className="le-stat-row">
            <StatTile label="Last sale" value={lastSale && isNum(lastSale.price_usd) ? fmtMoney(lastSale.price_usd) : DASH} sub={lastSale ? `${fmtDate(lastSale.date)} · ${lastSale.source === 'county_recorder_documents' ? 'county record' : 'listing record'}${lastSale.transaction_type ? ` · ${titleCase(lastSale.transaction_type)}` : ''}` : 'No priced sale on record'} emphasis />
            <StatTile label="Assessed value" value={fmtMoney(data.assessed_value_usd)} sub={isNum(data.annual_tax_usd) ? `${fmtMoney(data.annual_tax_usd)} tax / yr${isNum(data.property_tax_rate_percent) ? ` · ${fmtPct(data.property_tax_rate_percent, 2)}` : ''}` : null} />
            <StatTile label="Recorded documents" value={String(transactions.length)} sub={foreclosures.length ? `${foreclosures.length} pre-foreclosure filing${foreclosures.length === 1 ? '' : 's'}` : 'No pre-foreclosure filings'} />
          </div>
          <FactGrid
            columns={3}
            items={[
              { label: 'APN', value: data.apn || null },
              { label: 'Property class', value: data.property_class || null },
              { label: 'Owner occupied', value: data.owner_occupied === true ? 'Yes' : data.owner_occupied === false ? 'No' : null },
            ]}
          />

          {foreclosures.length ? (
            <Note tone="critical">
              <strong>{foreclosures.length} pre-foreclosure record{foreclosures.length === 1 ? '' : 's'} on file.</strong>
              <ul className="le-warning-list">
                {foreclosures.map((f, i) => (
                  <li key={i}>
                    {[f.recording_date ? `Recorded ${fmtDate(f.recording_date)}` : null, f.auction_date_time ? `Auction ${f.auction_date_time}` : null, f.trustee ? `Trustee ${f.trustee}` : null, f.beneficiary ? `Beneficiary ${f.beneficiary}` : null].filter(Boolean).join(' · ') || 'Details not provided'}
                  </li>
                ))}
              </ul>
            </Note>
          ) : (
            <div className="le-inline-ok">No pre-foreclosure records on file.</div>
          )}

          <SubHeading>Recorded documents</SubHeading>
          {transactions.length ? (
            <ol className="le-timeline le-timeline-deeds">
              {transactions.map((t, i) => (
                <li key={`${t.document_number || i}-${t.transaction_date}`} className="le-timeline-item">
                  <span className="le-timeline-date">
                    {fmtDate(t.recording_date || t.transaction_date)}
                    {t.recording_date && t.transaction_date && t.recording_date !== t.transaction_date ? <span className="le-timeline-date-sub">signed {fmtDate(t.transaction_date)}</span> : null}
                  </span>
                  <span className="le-timeline-body">
                    <span className="le-timeline-title">
                      <Pill tone={transactionTone(t.transaction_type)}>{t.transaction_type ? titleCase(t.transaction_type) : 'Transfer'}</Pill>
                      {isNum(t.sale_price_usd) ? <strong className="le-timeline-price">{fmtMoney(t.sale_price_usd)}</strong> : <span className="muted">No price recorded</span>}
                    </span>
                    <span className="le-timeline-meta">
                      {t.deed_type ? <Chip title="Deed type">{t.deed_type}</Chip> : null}
                      {t.document_number ? <Chip mono title="Document number">Doc {t.document_number}</Chip> : null}
                      {t.title_company && !/none/i.test(t.title_company) ? <span className="muted">{t.title_company}</span> : null}
                    </span>
                    <MortgageLine mortgage={t.mortgage} />
                  </span>
                </li>
              ))}
            </ol>
          ) : (
            <Note tone="muted">No recorded transactions were returned for this parcel.</Note>
          )}

          {priceHistory.length ? (
            <>
              <SubHeading>Listing price history</SubHeading>
              <ol className="le-timeline">
                {priceHistory.slice(0, 10).map((ev, i) => (
                  <li key={`${ev.date}-${ev.event}-${i}`} className="le-timeline-item">
                    <span className="le-timeline-date">{fmtDate(ev.date)}</span>
                    <span className="le-timeline-body">
                      <span className="le-timeline-title">{ev.event || 'Event'}</span>
                      <span className="le-timeline-meta">
                        {isNum(ev.price_usd) ? <strong>{fmtMoney(ev.price_usd)}</strong> : <span className="muted">No price</span>}
                        {isNum(ev.price_per_sqft_usd) ? <span> · {fmtMoney(ev.price_per_sqft_usd)}/sq ft</span> : null}
                        {ev.source ? <span className="muted"> · {ev.source}</span> : null}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
              {priceHistory.length > 10 ? <div className="le-more">+ {priceHistory.length - 10} earlier events</div> : null}
            </>
          ) : null}

          {taxHistory.length ? (
            <Disclosure summary={`Tax & assessment history · ${taxHistory.length} years`}>
              <div className="le-table-wrap le-table-compact">
                <table className="le-table">
                  <thead><tr><th>Year</th><th className="num">Assessed value</th><th className="num">Tax paid</th></tr></thead>
                  <tbody>
                    {taxHistory.map((t) => (
                      <tr key={t.year}><td>{t.year}</td><td className="num">{fmtMoney(t.assessed_value_usd)}</td><td className="num">{fmtMoney(t.tax_paid_usd)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Disclosure>
          ) : null}

          {notes.length ? <div className="le-footnotes">{notes.map((n) => <div key={n}>{n}</div>)}</div> : null}
          {data.retrieved_at ? <div className="le-footnotes muted">Retrieved {fmtDate(data.retrieved_at)}</div> : null}
        </>
      ) : null}
    </SectionCard>
  );
}

export { DASH };
